import FullCalendar, {
  CustomContentGenerator,
  EventApi,
  EventClickArg,
  EventContentArg,
  MountArg,
} from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  Event,
  TimeZone,
  Place,
  VirtualLocation,
  Filters,
  DurationFilter,
  PhysicalLocationFilter,
  VirtualLocationFilter,
  SelectedLocation,
  Opening,
  FlexCalSummary,
  FlexCal,
  Distance,
  ExternalCalendarEventSummary,
  Reservation,
  ExternalCalendar,
} from '../../utils/types';
import { DateTime, Duration } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import {
  LoadingOrigin,
  PhysicalVirtualLocationEnum,
  ViewType,
} from '../../utils/enums';
import { ClockLoader } from 'react-spinners';
import { LoadingContext } from '../../utils/contexts/loadingContext';
import isBookedExternally from '../../utils/helpers/isBookedExternally';
import isBookedInternally from '../../utils/helpers/isBookedInternally';
import handleDST from '../../utils/helpers/handleDST';
import { UserProfile, useUser } from '@auth0/nextjs-auth0';
import { UserInfo } from '../../utils/interfaces';
import styles from './Calendar.module.css';
import * as Sentry from '@sentry/nextjs';

type Props = {
  flexCal: FlexCalSummary;
  flexCalOpenings: Opening[] | undefined;
  externalEvents: ExternalCalendarEventSummary[] | undefined;
  reservations: Reservation[] | undefined;
  timeZone: TimeZone;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  isEventClicked: boolean;
  setIsEventClicked: React.Dispatch<React.SetStateAction<boolean>>;
  clickedEvent: EventApi | undefined;
  setClickedEvent: React.Dispatch<React.SetStateAction<EventApi | undefined>>;
  selectedLocation: SelectedLocation;
  onDateRangeChange: React.Dispatch<React.SetStateAction<string>>;
  setCurrentView: React.Dispatch<React.SetStateAction<ViewType>>;
  calendarHeight: number | undefined;
  calendarWidth: number | undefined;
  participantEvents: ExternalCalendarEventSummary[] | undefined;
  externalCalendars: ExternalCalendar[] | undefined;
  userLocation: UserInfo;
  maxTravelTimeMinutes: number;
  flexCalName: string;
  subscriberName: string;
  setIsTravelCalendarLoading: (e: boolean) => void;
};

const tempFilters: Filters = {
  durations: [],
  physicalLocations: [],
  virtualLocations: [],
};

let earliestTimeSlot = 8;

const TravelAwareCalendar = React.forwardRef<FullCalendar, Props>(
  (props, calendarRef) => {
    console.log('🚗 TravelAwareCalendar component is being rendered!');

    const { user } = useUser();
    const userInfo = user as UserInfo;
    const { loading } = useContext(LoadingContext);

    const [localFlexCalOpenings, setLocalFlexCalOpenings] = useState<
      Opening[] | undefined
    >(undefined);

    const [localDistances, setLocalDistances] = useState<
      Distance[] | undefined
    >(undefined);

    const [isFetchingTravelData, setIsFetchingTravelData] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Calculate initial date range immediately on mount
    useEffect(() => {
      if (props.onDateRangeChange) {
        // Calculate the current week's date range immediately
        const today = DateTime.now();
        const startOfWeek = today.startOf('week'); // Monday
        const endOfWeek = startOfWeek.plus({ days: 6 }); // Sunday

        let dateRange: string;
        if (startOfWeek.month === endOfWeek.month) {
          dateRange = `${startOfWeek.toFormat('MMM d')} – ${endOfWeek.toFormat(
            'd'
          )}`;
        } else {
          dateRange = `${startOfWeek.toFormat('MMM d')} – ${endOfWeek.toFormat(
            'MMM d'
          )}`;

          props.onDateRangeChange(dateRange);
        }
      }
    }, [props.onDateRangeChange]); // Only run once on mount

    useEffect(() => {
      const fetchTravelAwareData = async () => {
        setFetchError(null);

        try {
          const subscriberName: string = props.subscriberName;
          const calendarName: string = props.flexCalName;
          const url = `/api/flexCals/getTravelFlexCal/${subscriberName}/${calendarName}`;
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const travelAwareCalendarData = await response.json();

          setLocalFlexCalOpenings(travelAwareCalendarData.availableTimes || []);
          setLocalDistances(travelAwareCalendarData.distances || []);
          setDataLoaded(true);
        } catch (error) {
          console.error('Error fetching travelAwareCalendarData:', error);
          setFetchError(
            error instanceof Error ? error.message : 'Unknown error'
          );
          setLocalFlexCalOpenings([]);
          setLocalDistances([]);
          setDataLoaded(true);
        } finally {
          setIsFetchingTravelData(false);
          props.setIsTravelCalendarLoading(false);
        }
      };

      if (props.flexCal?.displayName && props.flexCal?.name) {
        fetchTravelAwareData();
      } else {
        setIsFetchingTravelData(false);
        props.setIsTravelCalendarLoading(false);
        setDataLoaded(true);
      }
    }, [props.flexCal?.displayName, props.flexCal?.name]);

    useEffect(() => {
      if (
        dataLoaded &&
        localFlexCalOpenings &&
        localFlexCalOpenings.length > 0
      ) {
        props.setFilters(tempFilters);
      }
    }, [dataLoaded, localFlexCalOpenings, props.setFilters]);

    const isLoading =
      !props.flexCal ||
      (loading?.isloading &&
        loading.loadingOrigin === LoadingOrigin.fullCalendar) ||
      isFetchingTravelData ||
      !dataLoaded ||
      props.externalEvents === undefined ||
      props.reservations === undefined;

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full bg-confirmed-secondary">
          <ClockLoader color="white" />
          <p className="mt-4 text-sm text-white">
            {isFetchingTravelData
              ? 'Loading travel-aware calendar data...'
              : 'Loading calendar...'}
          </p>
        </div>
      );
    }

    if (fetchError) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full bg-red-50 p-4">
          <div className="text-red-600 text-center">
            <p className="font-semibold">Error loading travel data</p>
            <p className="text-sm mt-2">{fetchError}</p>
            <button
              onClick={() => {
                try {
                  if (typeof window !== 'undefined' && window.location) {
                    window.location.reload();
                  }
                } catch (error) {
                  console.warn('Failed to reload page:', error);
                  Sentry.captureException(error);
                }
              }}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    const handleEventClick = (event: EventClickArg) => {
      props.setClickedEvent(event.event);
      props.setIsEventClicked(true);
      props.setCurrentView(ViewType.Day);
    };

    const getStartDateValue = (flexCal: FlexCalSummary | FlexCal) => {
      const startDateValue = flexCal?.evergreen
        ? new Date()
        : flexCal?.startDate;
      return startDateValue;
    };

    const getEndDateValue = (flexCal: FlexCalSummary | FlexCal) => {
      const endDateValue = flexCal?.evergreen
        ? DateTime.fromISO(flexCal.endDate)
            .endOf('week')
            .minus({ days: 1 })
            .toString()
        : DateTime.fromISO(flexCal?.endDate).toString();
      return endDateValue;
    };

    const events = createTimeSlots(
      props.flexCal,
      localFlexCalOpenings || [],
      props.externalEvents || [],
      props.participantEvents,
      props.externalCalendars,
      props.reservations || [],
      localDistances || [],
      props.filters,
      props.timeZone,
      props.clickedEvent,
      props.selectedLocation,
      user
    );

    const handleDatesSet = (dateInfo: any) => {
      if (props.onDateRangeChange) {
        const startDate = DateTime.fromJSDate(dateInfo.start);
        const endDate = DateTime.fromJSDate(dateInfo.end).minus({ days: 1 });

        let dateRange: string;
        if (startDate.month === endDate.month) {
          dateRange = `${startDate.toFormat('MMM d')} – ${endDate.toFormat(
            'd'
          )}`;
        } else {
          dateRange = `${startDate.toFormat('MMM d')} – ${endDate.toFormat(
            'MMM d'
          )}`;
        }
        props.onDateRangeChange(dateRange);
      }
    };

    return (
      <>
        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin]}
          editable={false}
          selectable
          nowIndicator={true}
          events={events}
          headerToolbar={{
            start: '',
            center: '',
            end: '',
          }}
          dayHeaders={true}
          businessHours={true}
          dayHeaderFormat={
            props.calendarWidth! <= 820
              ? {
                  weekday: 'narrow',
                  day: 'numeric',
                }
              : {
                  weekday: 'long',
                  day: 'numeric',
                }
          }
          initialView={'timeGridWeek'}
          initialDate={new Date()}
          height={props.calendarHeight === 240 ? '170px' : 'calc(100vh - 12em)'}
          allDaySlot={false}
          scrollTimeReset={false}
          validRange={{
            start: getStartDateValue(props.flexCal),
            end: getEndDateValue(props.flexCal),
          }}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: false,
          }}
          displayEventEnd={false}
          slotEventOverlap={false}
          eventDisplay={'block'}
          datesSet={handleDatesSet}
          eventMouseEnter={(mouseEnterInfo) => {
            if (mouseEnterInfo.event.extendedProps.type === 'subscriber') {
              mouseEnterInfo.el.style.backgroundColor = '#40978F';
              mouseEnterInfo.el.style.filter =
                'drop-shadow(0 0 0.75rem #86E3A8)';
            }
          }}
          eventMouseLeave={(mouseExitInfo) => {
            if (mouseExitInfo.event.extendedProps.type === 'subscriber') {
              if (mouseExitInfo.event.id !== 'clicked') {
                mouseExitInfo.el.style.backgroundColor = '#FEFEFF';
              } else {
                mouseExitInfo.el.style.backgroundColor = '#86E3A8';
              }
              mouseExitInfo.el.style.filter = 'none';
            }
          }}
          eventClick={(arg) => {
            if (arg.event.extendedProps.type === 'subscriber') {
              handleEventClick(arg);
            }
          }}
          eventClassNames={(eventContentArg) => {
            if (eventContentArg.event.extendedProps.type === 'participant') {
              return `${styles.participantEvent}`;
            } else {
              return '';
            }
          }}
          eventDidMount={(
            arg: MountArg<EventContentArg>
          ): CustomContentGenerator<EventContentArg> => {
            const eventParent = arg.el.parentElement;
            const events = arg.view.calendar.getEvents();
            const overlapEvents = events.filter(
              (e) =>
                e.start &&
                e.end &&
                arg.event.start &&
                arg.event.end &&
                e.end > arg.event.start &&
                arg.event.end > e.start
            );
            let distinctDurations: number[] = [];
            overlapEvents.forEach((e) => {
              if (e.start && e.end) {
                const duration = DateTime.fromJSDate(e.end).diff(
                  DateTime.fromJSDate(e.start),
                  'minutes'
                );

                if (!distinctDurations.includes(duration.toMillis())) {
                  if (e.extendedProps.type !== 'participant') {
                    distinctDurations.push(duration.toMillis());
                  } else if (!distinctDurations.includes(0)) {
                    distinctDurations.push(0);
                  }
                }
              }
            });
            let eventDuration = 0;
            if (
              arg.event.extendedProps.type !== 'participant' &&
              arg.event.end &&
              arg.event.start
            ) {
              eventDuration = DateTime.fromJSDate(arg.event.end)
                .diff(DateTime.fromJSDate(arg.event.start), 'minutes')
                .toMillis();
            }
            if (!distinctDurations.includes(eventDuration)) {
              distinctDurations.push(eventDuration);
            }
            distinctDurations = distinctDurations.sort((a, b) => b - a);
            if (eventParent) {
              if (arg.event.extendedProps.type === 'participant') {
                eventParent.classList.add(
                  `${styles.participantEventContainer}`
                );
              } else if (distinctDurations.includes(0)) {
                if (distinctDurations.length === 1) {
                  eventParent.classList.add(`${styles.none}`);
                } else if (distinctDurations.length === 2) {
                  getSlotPositioning(
                    eventDuration,
                    distinctDurations,
                    eventParent
                  );
                  eventParent.classList.add(`${styles.one}`);
                } else if (distinctDurations.length === 3) {
                  getSlotPositioning(
                    eventDuration,
                    distinctDurations,
                    eventParent
                  );
                  eventParent.classList.add(`${styles.two}`);
                } else if (distinctDurations.length === 4) {
                  getSlotPositioning(
                    eventDuration,
                    distinctDurations,
                    eventParent
                  );
                  eventParent.classList.add(`${styles.three}`);
                } else if (distinctDurations.length === 5) {
                  getSlotPositioning(
                    eventDuration,
                    distinctDurations,
                    eventParent
                  );
                  eventParent.classList.add(`${styles.four}`);
                } else if (distinctDurations.length === 6) {
                  getSlotPositioning(
                    eventDuration,
                    distinctDurations,
                    eventParent
                  );
                  eventParent.classList.add(`${styles.five}`);
                }
              }
            }
            return undefined;
          }}
          ref={calendarRef}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 0,
            marginBottom: 16,
            padding: '10px 0',
            maxWidth: 900,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style={{
              height: 22,
              width: 22,
              marginRight: 10,
              marginLeft: 8,
              flexShrink: 0,
            }}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            />
          </svg>
          <span
            style={{
              fontStyle: 'italic',
              color: '#222',
              fontSize: '1.05rem',
              lineHeight: 1.5,
            }}
          >
            "This scheduler automatically considers travel time so our meeting
            can start on time and without stress."
          </span>
        </div>
      </>
    );
  }
);

export default TravelAwareCalendar;

const createTimeSlots = (
  flexCal: FlexCalSummary,
  flexCalOpenings: Opening[],
  externalEvents: ExternalCalendarEventSummary[],
  participantEvents: ExternalCalendarEventSummary[] | undefined,
  externalCalendars: ExternalCalendar[] | undefined,
  reservations: Reservation[],
  distances: Distance[],
  filters: Filters,
  timeZone: TimeZone,
  clickedEvent: EventApi | undefined,
  selectedLocation: SelectedLocation,
  user: UserProfile | undefined
): Event[] => {
  const events: Event[] = [];
  const bookingBuffer: number | undefined = flexCal?.firstAvailableTimeInHours;

  tempFilters.durations = [];
  tempFilters.physicalLocations = [];
  tempFilters.virtualLocations = [];

  const allExtendedReservations: Reservation[] = [];
  const allStartTimes = new Set<string>();

  flexCalOpenings.forEach((opening) => {
    opening.reservations.forEach((res) => {
      if (res.reservationId === 0 && !allStartTimes.has(res.startTime)) {
        allExtendedReservations.push(res);
        allStartTimes.add(res.startTime);
      }
    });

    const openingDuration = DateTime.fromISO(opening.endDate).diff(
      DateTime.fromISO(opening.startDate),
      'minutes'
    );

    opening.durations?.forEach((duration) => {
      const durationAsMin = Duration.fromISOTime(duration).as('minutes');
      setFilters(
        durationAsMin,
        opening.physicalLocations,
        opening.virtualLocations
      );

      if (filterByMeetingLength(durationAsMin, filters)) {
        return;
      }

      const allPossiblePhysicalLocations = filterByPhysicalLocation(
        opening.physicalLocations,
        filters,
        selectedLocation
      );
      const filteredPhysical = allPossiblePhysicalLocations.length === 0;
      const filteredVirtual = filterByVirtualLocation(
        opening.virtualLocations,
        filters,
        selectedLocation
      );

      if (filteredPhysical && filteredVirtual) {
        return;
      }

      const slotDuration = Duration.fromISOTime(duration);
      const numTimeSlots = openingDuration.toMillis() / slotDuration.toMillis();
      let previousEndTime = DateTime.fromISO(opening.startDate);

      for (let i = 0; i < numTimeSlots; i++) {
        const newEndTime = previousEndTime.plus(slotDuration);

        const isReserved = opening.reservations.some((reservation) => {
          const reservationStart = DateTime.fromISO(
            reservation.startTime
          ).toMillis();
          const reservationEnd = DateTime.fromISO(
            reservation.endTime
          ).toMillis();
          return (
            previousEndTime.toMillis() < reservationEnd &&
            newEndTime.toMillis() > reservationStart
          );
        });

        const shouldCheckPhysicalOverlap =
          selectedLocation.locationType !==
            PhysicalVirtualLocationEnum.virtual &&
          allPossiblePhysicalLocations.length > 0;

        const physicalLocationsPos: Place[] = [];
        let isOverlapped = false;

        if (shouldCheckPhysicalOverlap) {
          const secondsBuffer = 60 * 10;
          const percentBuffer = 1.1;
          let allLocationsOverlap = true;

          allPossiblePhysicalLocations.forEach((location) => {
            const addHash2 = location.addressHash;
            let overlapFound = false;

            allExtendedReservations.forEach((reservation) => {
              if (reservation.physicalLocation) {
                const addHash1 = reservation.physicalLocation.addressHash;
                const reservationStart = DateTime.fromISO(
                  reservation.startTime
                ).toMillis();
                const reservationEnd = DateTime.fromISO(
                  reservation.endTime
                ).toMillis();
                const newStartTime = previousEndTime.toMillis();
                const newEndTimeMillis = newEndTime.toMillis();

                const distanceObj = distances?.find(
                  (d) =>
                    (d.address1Hash === addHash1 &&
                      d.address2Hash === addHash2) ||
                    (d.address1Hash === addHash2 && d.address2Hash === addHash1)
                );

                if (distanceObj) {
                  const bufferedDistance =
                    (distanceObj.distanceBetween + secondsBuffer) *
                    percentBuffer;
                  if (reservationStart < newStartTime) {
                    if (
                      reservationEnd + bufferedDistance * 1000 >
                      newStartTime
                    ) {
                      overlapFound = true;
                    }
                  } else {
                    if (
                      newEndTimeMillis + bufferedDistance * 1000 >
                      reservationStart
                    ) {
                      overlapFound = true;
                    }
                  }
                }
              }
            });

            if (!overlapFound) {
              physicalLocationsPos.push(location);
              allLocationsOverlap = false;
            }
          });

          isOverlapped = allLocationsOverlap;
        } else {
          // When only virtual location is selected or no physical locations available,
          // use the original physical locations from opening (or empty array)
          physicalLocationsPos.push(...allPossiblePhysicalLocations);
          isOverlapped = false;
        }

        const eventStart = previousEndTime.setZone(timeZone.tzdbId);
        const eventEnd = newEndTime.setZone(timeZone.tzdbId);

        const isClickedEvent =
          opening.openingId === clickedEvent?.extendedProps.openingId &&
          i === clickedEvent?.extendedProps.timeSlotIdx &&
          duration === clickedEvent?.extendedProps.duration;

        const timeSlot: Event = {
          start: flexCal.evergreen
            ? handleDST(eventStart, timeZone).toString()
            : eventStart.toString(),
          end: flexCal.evergreen
            ? handleDST(eventEnd, timeZone).toString()
            : eventEnd.toString(),
          backgroundColor: isClickedEvent ? '#86E3A8' : '#FEFEFF',
          borderColor: '#40978F',
          textColor: '#000000',
          id: isClickedEvent ? 'clicked' : undefined,
          extendedProps: {
            physicalLocations: physicalLocationsPos,
            virtualLocations: opening.virtualLocations,
            openingId: opening.openingId,
            timeSlotIdx: i,
            interactive: 'Y',
            duration: duration,
            type: 'subscriber',
            startEditable: true,
          },
        };

        if (previousEndTime.hour < earliestTimeSlot)
          earliestTimeSlot = previousEndTime.hour;

        if (
          !isReserved &&
          !isOverlapped &&
          previousEndTime.toMillis() >=
            DateTime.now().plus({ hours: bookingBuffer }).toMillis() &&
          !isBookedExternally(eventStart, eventEnd, externalEvents) &&
          !isBookedInternally(eventStart, eventEnd, reservations)
        ) {
          events.push(timeSlot);
        }

        previousEndTime = newEndTime;
      }
    });
  });

  participantEvents?.forEach((event) => {
    const eventStart = DateTime.fromISO(event.startDate).setZone(
      timeZone.tzdbId
    );
    const eventEnd = DateTime.fromISO(event.endDate).setZone(timeZone.tzdbId);

    const timeSlot: Event = {
      start: handleDST(eventStart, timeZone).toString(),
      end: handleDST(eventEnd, timeZone).toString(),
      backgroundColor: getSlotColor(user?.sub, event, externalCalendars),
      borderColor: getSlotColor(user?.sub, event, externalCalendars),
      textColor: getSlotColor(user?.sub, event, externalCalendars),
      extendedProps: {
        type: 'participant',
      },
      overlap: true,
      startEditable: true,
    };
    events.push(timeSlot);
  });

  return events;
};

const setFilters = (
  durationAsMin: number,
  physicalLocations: Place[],
  virtualLocations: VirtualLocation[]
): void => {
  if (
    tempFilters.durations.findIndex(
      (durationFilter: DurationFilter) =>
        durationFilter.duration === durationAsMin
    ) === -1
  ) {
    tempFilters.durations.push({ duration: durationAsMin, isChecked: true });
  }

  physicalLocations.forEach((location) => {
    if (
      tempFilters.physicalLocations.findIndex(
        (locationFilter: PhysicalLocationFilter) =>
          locationFilter.placeId === location.placeId
      ) === -1
    ) {
      tempFilters.physicalLocations.push({
        placeId: location.placeId,
        name: location.name,
        address1: location.address1,
        address2: location.address2,
        city: location.city,
        state: location.state,
        postalCode: location.postalCode,
      });
    }
  });

  virtualLocations.forEach((location) => {
    if (
      tempFilters.virtualLocations.findIndex(
        (locationFilter: VirtualLocationFilter) =>
          locationFilter.virtualLocationId === location.virtualLocationId
      ) === -1
    ) {
      tempFilters.virtualLocations.push({
        virtualLocationId: location.virtualLocationId,
        name: location.label,
      });
    }
  });
};

const filterByMeetingLength = (duration: number, filters: Filters): boolean => {
  const durationIndex = filters.durations.findIndex(
    (durationFilter) => durationFilter.duration === duration
  );

  if (durationIndex === -1) {
    return false;
  }

  return !filters.durations[durationIndex].isChecked;
};

const filterByPhysicalLocation = (
  inputPhysicalLocations: Place[],
  filters: Filters,
  selectedLocation: SelectedLocation
): Place[] => {
  if (selectedLocation.id === 0) return inputPhysicalLocations;

  const selectedIds = filters.physicalLocations.map((loc) => loc.placeId);
  const intersection = inputPhysicalLocations.filter((place) =>
    selectedIds.includes(place.placeId)
  );

  if (selectedLocation.locationType === PhysicalVirtualLocationEnum.physical) {
    return intersection.filter(
      (place) => place.placeId === selectedLocation.id
    );
  }

  return intersection;
};

const filterByVirtualLocation = (
  inputVirtualLocations: VirtualLocation[],
  filters: Filters,
  selectedLocation: SelectedLocation
): boolean => {
  if (selectedLocation.id === 0) return false;
  for (let i = 0; i < inputVirtualLocations.length; i++) {
    const locationIndex = filters.virtualLocations.findIndex(
      (locationFilter) =>
        locationFilter.virtualLocationId ===
        inputVirtualLocations[i].virtualLocationId
    );

    if (locationIndex === -1) {
      continue;
    }
    if (
      selectedLocation.locationType === PhysicalVirtualLocationEnum.virtual &&
      filters.virtualLocations[locationIndex].virtualLocationId ===
        selectedLocation.id
    ) {
      return false;
    }
  }

  return true;
};

const getSlotPositioning = (
  duration: number,
  durations: number[],
  eventParent: HTMLElement
) => {
  if (durations[0] === duration) {
    return eventParent.classList.add(`${styles.first}`);
  }
  if (durations.length === 3) {
    return eventParent.classList.add(`${styles.oneHalf}`);
  }
  if (durations.length === 4) {
    if (durations[1] === duration) {
      return eventParent.classList.add(`${styles.oneThird}`);
    } else {
      return eventParent.classList.add(`${styles.twoThirds}`);
    }
  }
  if (durations.length === 5) {
    if (durations[1] === duration) {
      return eventParent.classList.add(`${styles.oneFourth}`);
    }
    if (durations[2] === duration) {
      return eventParent.classList.add(`${styles.oneHalf}`);
    } else {
      return eventParent.classList.add(`${styles.threeFourths}`);
    }
  }
  if (durations.length === 6) {
    if (durations[1] === duration) {
      return eventParent.classList.add(`${styles.oneFifth}`);
    }
    if (durations[2] === duration) {
      return eventParent.classList.add(`${styles.twoFifths}`);
    }
    if (durations[3] === duration) {
      return eventParent.classList.add(`${styles.threeFifths}`);
    } else {
      return eventParent.classList.add(`${styles.fourFifths}`);
    }
  }
};

const getSlotColor = (
  userName: string | undefined | null,
  event: ExternalCalendarEventSummary | undefined,
  externalCalendars: ExternalCalendar[] | undefined
) => {
  if (externalCalendars && externalCalendars?.length > 0 && event) {
    const provider = externalCalendars.find(
      (ec) => ec.name === event.calendarName
    )?.provider;
    if (provider === 'Google') return '#F9A825';
    if (provider === 'Free/Busy') return '#87E3A8';
    if (provider === 'Microsoft') return '#0078D4';
  } else {
    if (!userName) return '#87E3A8';
    if (userName.includes('waad')) return '#0078D4';
    if (userName.includes('google')) return '#F9A825';
  }
  return '#87E3A8';
};
